import { useMemo, useState } from 'react';
import { useAlarmHistory, useCreateExport, useExportLink, useLabEntries, useProduction } from '@/lib/query';
import { REPORT_SPECS, buildReport, type ReportConfig, type ReportDataset } from '@/lib/report';
import { Badge, Card, EmptyHint, SectionTitle } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

const EXPORT_DATASET: Record<ReportDataset, string> = { alarms: 'alarms', lab: 'lab', production: 'production-report' };

export default function ReportBuilder() {
  const [dataset, setDataset] = useState<ReportDataset>('alarms');
  const spec = REPORT_SPECS[dataset];
  const [cols, setCols] = useState<string[]>(spec.columns.map((c) => c.key));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [aggCol, setAggCol] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const { data: alarmHistory } = useAlarmHistory();
  const { data: lab } = useLabEntries();
  const { data: prod } = useProduction();
  const create = useCreateExport();
  const link = useExportLink();

  const rows = useMemo<Record<string, unknown>[]>(() => {
    if (dataset === 'alarms') return (alarmHistory ?? []) as unknown as Record<string, unknown>[];
    if (dataset === 'lab') return (lab?.effective ?? []) as unknown as Record<string, unknown>[];
    return (prod?.batches ?? []) as unknown as Record<string, unknown>[];
  }, [dataset, alarmHistory, lab, prod]);

  const pickDataset = (d: ReportDataset) => {
    setDataset(d);
    setCols(REPORT_SPECS[d].columns.map((c) => c.key));
    setGroupBy('');
    setAggCol('');
  };

  const cfg: ReportConfig = { columns: cols, from: from || undefined, to: to || undefined, groupBy: groupBy || undefined, aggCol: aggCol || undefined };
  const table = useMemo(() => buildReport(rows, dataset, cfg), [rows, dataset, cols, from, to, groupBy, aggCol]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCol = (k: string) => setCols((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));

  const doExport = () => {
    create.mutate(
      { purpose: `L2 报表：${spec.label}${from ? ` ${from}~${to || ''}` : ''}`, format: 'CSV', dataset: EXPORT_DATASET[dataset] as 'alarms' | 'lab' | 'production' | 'production-report' },
      {
        onSuccess: (job) => {
          link.mutateAsync(job.id).then(({ url }) => {
            const a = document.createElement('a');
            a.href = url;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setMsg(`已导出 ${spec.label} 数据集（${job.rowCount ?? rows.length} 行，经合规导出链路）`);
          });
        },
        onError: (e) => setMsg(`导出失败：${(e as Error)?.message ?? '未知'}`),
      },
    );
  };

  const numericCols = spec.columns.filter((c) => c.numeric);

  return (
    <div>
      <PageBanner />
      <div className="mb-5">
        <h2 className="text-[22px] font-bold text-slate-900">报表构建器</h2>
        <p className="mt-0.5 text-[13px] text-slate-500">选数据集 → 勾选列 / 时间范围 / 分组聚合 → 预览，并可经合规导出链路导出。</p>
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(REPORT_SPECS) as ReportDataset[]).map((d) => (
            <button key={d} onClick={() => pickDataset(d)} className={d === dataset ? 'rounded-lg border border-brand-500 bg-brand-50 px-3 py-1.5 text-[13px] font-medium text-brand-700' : 'rounded-lg border border-hairline px-3 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50'}>
              {REPORT_SPECS[d].label}
            </button>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <label className="text-[12px] text-slate-500">起始<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-2 py-1.5 text-sm" /></label>
          <label className="text-[12px] text-slate-500">结束<input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-2 py-1.5 text-sm" /></label>
          <label className="text-[12px] text-slate-500">分组依据
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-2 py-1.5 text-sm">
              <option value="">不分组（明细）</option>
              {spec.columns.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </label>
          <label className="text-[12px] text-slate-500">聚合值(求和)
            <select value={aggCol} onChange={(e) => setAggCol(e.target.value)} disabled={!groupBy} className="mt-1 w-full rounded-lg border border-hairline px-2 py-1.5 text-sm disabled:opacity-50">
              <option value="">仅计数</option>
              {numericCols.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </label>
        </div>

        {!groupBy ? (
          <div className="mt-3">
            <div className="mb-1 text-[12px] text-slate-400">显示列</div>
            <div className="flex flex-wrap gap-2">
              {spec.columns.map((c) => (
                <button key={c.key} onClick={() => toggleCol(c.key)} className={cols.includes(c.key) ? 'rounded-md border border-brand-500 bg-brand-50 px-2 py-1 text-[12px] text-brand-700' : 'rounded-md border border-hairline px-2 py-1 text-[12px] text-slate-400'}>
                  {cols.includes(c.key) ? '✓ ' : ''}{c.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </Card>

      <div className="mb-3 flex items-center justify-between">
        <SectionTitle title={`报表预览 · ${table.rows.length} 行`} right={<button onClick={doExport} disabled={create.isPending} className="btn btn-primary disabled:opacity-60">{create.isPending ? '导出中…' : '导出 (E 链路)'}</button>} />
      </div>
      {msg ? <div className="mb-3 rounded-lg bg-ok-bg/60 px-3 py-2 text-[13px] text-ok-fg">{msg}</div> : null}

      <Card className="p-0">
        {table.rows.length === 0 ? (
          <div className="p-4"><EmptyHint>无匹配数据（试试放宽时间范围或换数据集）</EmptyHint></div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-hairline bg-slate-50/60">
                {table.headers.map((h) => (<th key={h} className="th">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {table.rows.slice(0, 200).map((r, i) => (
                <tr key={i} className="border-b border-hairline/70 last:border-0">
                  {r.map((cell, j) => (<td key={j} className="td tabular-nums">{String(cell)}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {table.rows.length > 200 ? <div className="mt-2 text-[12px] text-slate-400"><Badge>预览仅前 200 行，导出含全部</Badge></div> : null}
    </div>
  );
}
